import { Link } from "wouter";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import SEOHead from "@/components/SEOHead";
import SharePostRow from "@/components/SharePostRow";
import Header from "@/components/Header";
import FaqAccordion from "@/components/FaqAccordion";
import Breadcrumbs from "@/components/Breadcrumbs";
import ServiceHubAuditCard from "@/components/ServiceHubAuditCard";
import { getServiceSchemaSupportingEntities } from "@/lib/siteEntityGraph";
import { SCORE_LABEL, UK_DELIVERY_NOTE } from "@/lib/serviceHubContracts";

import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import {
  Search,
  MessageSquare,
  Globe,
  BarChart2,
  Bot,
  CheckCircle,
  ArrowRight,
  Layers,
  TrendingUp,
  Zap,
  Users,
  Target,
  Shield,
} from "lucide-react";
import ScoreInlineForm from "@/components/ScoreInlineForm";

import SchemaScript from "@/components/SchemaScript";
const CANONICAL = "https://integrated.social/services/integrated-marketing-communications/";
const PAGE_TITLE = "Integrated Marketing Communications Agency London | IMC";
const PAGE_DESCRIPTION = "Integrated marketing communications agency in London: one message across paid, owned and earned channels, with strategy, content and campaigns.";
const IMC_HUB_FIRST_ANSWER = "Integrated marketing communications means one message, one buyer persona and one attribution model across SEO, AEO, GEO, PPC and agentic AI, instead of five suppliers reporting five versions of success. Integrated.Social runs these channels as one program for B2B teams in London and the UK, with a named senior person signing off the work.";
const IMC_PRICE_LINE = "IMC programs from £6,500 per month including VAT. Scope is confirmed on a short call.";
const IMC_ANSWER_BLOCK = `${IMC_HUB_FIRST_ANSWER} ${IMC_PRICE_LINE}`;
const IMC_FAQ_SUMMARY = "These answers cover what integrated marketing communications means for a B2B team, how it differs from hiring several agencies, how attribution works across channels, how long the work takes to show evidence and what it costs. In short, one persona and one attribution model sit across every channel, the program is scoped on a short call, and progress is reported against the same dated baseline every month.";

const faqSchema = [
  {
    q: "What is an integrated marketing communications agency?",
    a: IMC_HUB_FIRST_ANSWER,
  },
  {
    q: "What must an integrated marketing communications agency be able to connect?",
    a: "It should connect strategy, creative, paid media, SEO, content, reporting and sales handoffs around a shared commercial brief. The test is not whether it lists every channel; it is whether the team can explain which buyer decision the work supports, what evidence is needed, who owns the handoff and how the commercial signal will be reviewed.",
  },
  {
    q: "How is an integrated marketing communications agency different from a specialist agency?",
    a: "A specialist agency focuses on one channel. An IMC agency coordinates channels around the same buyer journey and attribution model. The practical difference is a shared brief, reporting method and owner for the handoffs. Attribution depends on the evidence available; it cannot establish every unseen touchpoint or prove that a particular AI citation caused a conversion.",
  },
  {
    q: "What does the Integrated.Social IMC Framework include?",
    a: "The Integrated.Social IMC Framework has five capabilities: (1) SEO Entity Authority, structured data and topical depth; (2) AEO Answer-First Content, content organized around buyer questions; (3) GEO Cross-Platform Citation Consistency, reviewing brand information across AI and search platforms; (4) PPC and Performance Max Amplification, paid activity using the same persona and intent data; and (5) Agentic AI Workflow Automation, agent-supported monitoring, research and content drafting. Agents draft; a named senior person signs off. Rankings and citations are not guaranteed.",
  },
  {
    q: "What does one measurement plan mean?",
    a: "One measurement plan gives the team agreed definitions for inquiries, qualified opportunities, costs and revenue. It also states which system owns each metric and how attribution is applied. It does not reveal every private research interaction or prove that each channel caused the conversions reported against it.",
  },
  {
    q: "Do you work with B2B or B2C companies?",
    a: "Primarily B2B. Our IMC framework is designed for companies with longer sales cycles, multiple buying committee members, and high-value contracts where attribution complexity is highest. We work with B2B technology, professional services, financial services, SaaS, and manufacturing companies across the UK, Europe, and North America. We do take on select B2C and ecommerce brands where the product has a considered purchase cycle and the buyer journey benefits from integrated channel management.",
  },
  {
    q: "What should a B2B SaaS company expect from an integrated digital marketing agency?",
    a: "A B2B SaaS team should expect one commercial plan that names the audience, buyer decision, approved messages, channels, evidence, sales handoffs, and review method. Integrated work can connect SEO, answer-ready content, paid media, and sales enablement when they support the same decision; scope and accountable owners should be agreed before work begins. It does not guarantee rankings, AI citations, leads, or revenue.",
  },
  {
    q: "How should a SaaS team choose between an integrated marketing program and account-based marketing?",
    a: "Choose an integrated marketing program when several channels need to support one buyer decision and shared measurement. Use account-based marketing when the priority is a defined set of accounts and their buying groups. The appropriate scope depends on the sales model, available evidence, approved claims, and delivery capacity.",
  },
  {
    q: "How long does it take to see results from an integrated marketing program?",
    a: "The timeline depends on starting position, scope and available evidence. There is no guaranteed date for rankings, citations, leads or commercial results. Each month's work is reported against the same dated baseline, so you can see what has moved and what has not.",
  },
  {
    q: "What does an integrated marketing communications program cost?",
    a: `${IMC_PRICE_LINE} We also offer outcome-based and pay-per-resolution pricing models for clients who prefer to align fees with commercial results rather than retainer hours.`,
  },
  {
    q: "Is integrated marketing communications relevant in the age of AI search?",
    a: "Yes. Buyers can encounter a brand across AI search, traditional search, paid media and social channels. An IMC framework coordinates the message, evidence and measurement across those channels. Answer-first content and consistent brand information can make pages easier to understand, but no agency can guarantee that an AI system will cite them.",
  },
  {
    q: "How do you measure AI citation share of voice?",
    a: "We monitor AI citation share of voice by running a structured set of target buyer queries through ChatGPT, Perplexity, Google AI Overviews, and Gemini on a monthly basis and recording which brands are cited, in what context, and with what sentiment. We track citation rate (percentage of queries where the brand appears), citation position (first mention vs. supporting mention), and citation sentiment (positive, neutral, or negative framing). This data feeds directly into the monthly attribution dashboard alongside organic and paid metrics.",
  },
];

const capabilities = [
  {
    icon: Search,
    number: "01",
    title: "SEO Entity Authority",
    color: "text-blue-400",
    bg: "bg-blue-400/10",
    border: "border-blue-400/20",
    description:
      "We build structured data, topical depth, and entity context to make your pages easier for Google and AI retrieval systems to understand. This includes appropriate Schema.org implementation, entity mapping, internal linking architecture, and Core Web Vitals optimization. Rankings and citations are not guaranteed.",
    outcomes: [
      "Organic keyword rankings for commercial-intent queries",
      "Entity recognition in Google Knowledge Graph",
      "Structured data coverage across all page types",
      "Core Web Vitals measurements",
    ],
  },
  {
    icon: MessageSquare,
    number: "02",
    title: "AEO Answer-First Content",
    color: "text-teal-400",
    bg: "bg-teal-400/10",
    border: "border-teal-400/20",
    description:
      "We structure content around the questions your buyers ask AI systems, with context-relevant FAQPage schema and answer-first paragraphs. The work makes pages easier to read and understand; it cannot guarantee citation in ChatGPT, Perplexity, or Google AI Overviews.",
    outcomes: [
      "Citation rate in ChatGPT and Perplexity responses",
      "Google AI Overview appearances for target queries",
      "FAQPage schema indexation rate",
      "Answer-first content coverage across the buyer journey",
    ],
  },
  {
    icon: Globe,
    number: "03",
    title: "GEO Cross-Platform Citations",
    color: "text-green-400",
    bg: "bg-green-400/10",
    border: "border-green-400/20",
    description:
      "We review brand information across relevant AI and search platforms for accuracy and consistency. This includes Google Business Profile optimization, Bing Places, Apple Maps, industry directories, Reddit community presence, and agency listing profiles. External platforms control how they use and display that information.",
    outcomes: [
      "Brand citation consistency across monitored AI platforms",
      "Google Business Profile completeness score",
      "Industry directory listing coverage",
      "AI share of voice across ChatGPT, Perplexity, Gemini",
    ],
  },
  {
    icon: BarChart2,
    number: "04",
    title: "PPC and Performance Max",
    color: "text-orange-400",
    bg: "bg-orange-400/10",
    border: "border-orange-400/20",
    description:
      "We structure paid channels and Performance Max campaigns around the same persona, intent data, approved messages and content calendar as the organic program. Paid and organic evidence is reviewed together without assuming that paid activity causes organic rankings or AI citations.",
    outcomes: [
      "Pipeline contribution from paid channels",
      "Cost per qualified lead from PPC",
      "Retargeting conversion rate from organic audiences",
      "Performance Max asset quality and conversion volume",
    ],
  },
  {
    icon: Bot,
    number: "05",
    title: "Agentic AI Automation",
    color: "text-purple-400",
    bg: "bg-purple-400/10",
    border: "border-purple-400/20",
    description:
      "AI agents support monitoring of unanswered buyer questions, research and content drafting, with citation observations reviewed monthly. Agents draft; a named senior person signs off. The program is not fully automatic.",
    outcomes: [
      "Monthly AI citation gap reports",
      "Agent-assisted content drafts with human sign-off",
      "Schema update frequency",
      "Time from gap identification to content live",
    ],
  },
];

const comparisonRows = [
  {
    dimension: "Attribution model",
    imc: "Single unified model across all channels",
    specialist: "Channel-focused; cross-channel scope should be checked",
    inhouse: "Depends on the team's data and integration scope",
  },
  {
    dimension: "AI citation tracking",
    imc: "Monthly share of voice across ChatGPT, Perplexity, Gemini",
    specialist: "Confirm whether citation tracking is included",
    inhouse: "Depends on the team's agreed method and tooling",
  },
  {
    dimension: "Channel integration",
    imc: "SEO, AEO, GEO, PPC, and agentic AI in one program",
    specialist: "Check who coordinates with other channel owners",
    inhouse: "Internal ownership of channel coordination",
  },
  {
    dimension: "Content strategy",
    imc: "Unified buyer journey mapped across all touchpoints",
    specialist: "Check alignment with the shared buyer journey",
    inhouse: "Depends on the team's planning method",
  },
  {
    dimension: "Speed to insight",
    imc: "Monthly unified dashboard with commentary",
    specialist: "Confirm reporting cadence and context",
    inhouse: "Depends on team capacity and reporting cadence",
  },
  {
    dimension: "Cost structure",
    imc: "Single retainer, outcome-based options available",
    specialist: "Per-channel retainer, multiplied by number of agencies",
    inhouse: "Salary overhead plus tool stack",
  },
  {
    dimension: "Accountability",
    imc: "One partner accountable for pipeline contribution",
    specialist: "Check channel ownership and sales handoffs",
    inhouse: "Internal ownership and review responsibilities",
  },
];

const buyerQuestions = [
  {
    question: "What evidence connects AI-referred visits to CRM outcomes, and where are the attribution gaps?",
    why: "Ask for the evidence, assumptions and named owner. A citation observation alone does not prove that a buyer saw it or that it caused a sale.",
  },
  {
    question: "Which of your clients has the highest AI citation share of voice in their category, and how did you achieve it?",
    why: "This tests whether they have a repeatable methodology for AI citation, or whether it is a side effect of their SEO work.",
  },
  {
    question: "How do you coordinate the content calendar between SEO, PPC, and social so they reinforce each other rather than compete?",
    why: "Genuine integration requires a shared content calendar and a shared brief. Agencies that run channels in silos will not have a clear answer.",
  },
  {
    question: "What does your monthly reporting cover, and who owns the attribution model?",
    why: "The answer reveals whether they have a unified view of performance or whether they will send you five separate reports and leave you to synthesize them.",
  },
  {
    question: "How do you handle the transition if we decide to bring one channel in-house?",
    why: "A mature integrated agency has a clear handover process. An agency that is defensive about this question is protecting channel revenue, not your interests.",
  },
];

export default function ServiceIMC() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonical={CANONICAL}
        locale="en-US"
        ogImage="https://integrated.social/media/service-imc-hub-hero_c8943003.png"
        keywords="integrated marketing communications agency London, integrated marketing communications agencies AI SEO services, IMC agency B2B, integrated digital marketing agency UK, unified marketing attribution B2B, full service AI marketing agency London"
      />

      {/* JSON-LD: Service + BreadcrumbList + Organization */}
      <SchemaScript schema={JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              ...getServiceSchemaSupportingEntities(),
              {
                "@type": "BreadcrumbList",
                "@id": `${CANONICAL}#breadcrumb`,
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://integrated.social/" },
                  { "@type": "ListItem", position: 2, name: "Services", item: "https://integrated.social/services/" },
                  { "@type": "ListItem", position: 3, name: "Integrated Marketing Communications Agency London", item: CANONICAL },
                ],
              },
              {
                "@type": "Service",
                "@id": `${CANONICAL}#service`,
                name: "Integrated Marketing Communications Agency London",
                description:
                  "Integrated marketing communications for B2B companies and selected B2C and ecommerce brands with considered purchase cycles: SEO, AEO, GEO, PPC and agent-supported workflows under one attribution model. Agents draft; a named senior person signs off.",
                url: CANONICAL,
                provider: { "@id": "https://integrated.social/#organization" },
                audience: [
                  { "@type": "BusinessAudience", audienceType: "B2B companies" },
                  { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
                ],
                areaServed: [
                  { "@type": "Country", name: "United Kingdom" },
                  { "@type": "City", name: "London" },
                ],
                serviceType: "Integrated Marketing Communications",
                offers: {
                  "@type": "Offer",
                  name: "IMC program",
                  priceCurrency: "GBP",
                  price: "6500",
                  valueAddedTaxIncluded: true,
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    price: "6500",
                    priceCurrency: "GBP",
                    unitCode: "MON",
                    unitText: "MONTH",
                    valueAddedTaxIncluded: true,
                  },
                  description: IMC_PRICE_LINE,
                  url: CANONICAL,
                  eligibleRegion: { "@type": "Country", name: "United Kingdom" },
                },
              },
              {
                "@type": "WebPage",
                "@id": `${CANONICAL}#webpage`,
                url: CANONICAL,
                name: PAGE_TITLE,
                headline: "Integrated Marketing Communications: One Agency, One Attribution Model.",
                description: PAGE_DESCRIPTION,
                abstract: IMC_ANSWER_BLOCK,
                dateModified: "2026-11-04",
                breadcrumb: { "@id": `${CANONICAL}#breadcrumb` },
                about: { "@id": "https://integrated.social/#organization" },
                mainEntity: { "@id": `${CANONICAL}#service` },
                publisher: { "@id": "https://integrated.social/#organization" },
                isPartOf: { "@type": "WebSite", "@id": "https://integrated.social/#website" },
                author: { "@id": "https://integrated.social/modi-elnadi/#person" },
                inLanguage: "en-US",
              },
              {
                "@type": "FAQPage",
                "@id": `${CANONICAL}#faq`,
                isPartOf: { "@id": `${CANONICAL}#webpage` },
                mainEntity: faqSchema.map((item) => ({
                  "@type": "Question",
                  name: item.q,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: item.a,
                  },
                })),
              },
            ],
          })} />

      <Header />
      <main>
      <div className="container px-4 pt-20">
        <Breadcrumbs
          includeSchema={false}
          items={[
            { label: "Services", href: "/services/" },
            { label: "Integrated Marketing Communications" },
          ]}
        />
      </div>
      {/* Hero */}
      <section className="relative overflow-hidden pt-24 pb-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 via-[#0a0a0f] to-purple-900/10 pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 text-teal-400 text-sm font-medium mb-6">
                <Layers className="w-3.5 h-3.5" />
                Integrated Marketing Communications
              </div>
                <h1 className="font-serif text-4xl lg:text-5xl leading-tight mb-6">
                Integrated Marketing Communications: One Agency, One Attribution Model.
              </h1>
              <p data-answer-block className="text-lg text-white/70 mb-4 leading-relaxed">
                {IMC_ANSWER_BLOCK}
              </p>
              <p className="text-base text-white/50 mb-8 leading-relaxed">
                {UK_DELIVERY_NOTE} Every channel we run shares the same buyer intelligence, the same content calendar, and the same attribution model. Reporting connects the available channel evidence without claiming to capture every buyer touchpoint.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/free-ai-growth-audit/" onClick={() => trackSectionCtaClick({ section_name: "service_imc_bottom_cta", cta_text: SCORE_LABEL, destination: "/free-ai-growth-audit/" })}>
                  <a className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold px-6 py-3 rounded-lg transition-colors">
                    {SCORE_LABEL}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Link>
                <a href="#framework" className="inline-flex items-center gap-2 border border-white/20 hover:border-teal-400/50 text-white/80 hover:text-white px-6 py-3 rounded-lg transition-colors">
                  See the Framework
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/media/service-imc-hub-hero_c8943003.png"
                alt="Integrated Marketing Communications framework by Integrated.Social: five capability towers (AEO/SEO, PPC, Agentic AI, ABM, Brand Strategy) connected to unified attribution"
                className="w-full rounded-2xl shadow-2xl shadow-teal-900/20"
                width={800}
                height={450}
              />
            </div>
            <ServiceHubAuditCard cluster="IMC" />
          </div>
        </div>
      </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text={IMC_ANSWER_BLOCK}
            title="Integrated Marketing Communications for B2B"
            readingTime={5}
          />
        </div>

      {/* The Problem */}
      <section className="py-16 px-4 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">The five-invoice problem</h2>
          <p className="text-lg text-white/70 leading-relaxed mb-8">
            When a B2B team hires separate agencies for SEO, PPC, content, social and AI strategy, each report can focus on its own channel metrics. Without a shared model, overlapping claims of credit make it difficult to judge how channels interact or which evidence supports a pipeline decision.
          </p>
          <p className="text-lg text-white/70 leading-relaxed">
            This is not a resourcing problem. It is an architecture problem. The solution is not to hire a better SEO agency or a better PPC agency. The solution is to integrate the channels under a single strategy, a single content calendar, and a single attribution model. That is what an integrated marketing communications program delivers.
          </p>
        </div>
      </section>

      {/* The Five-Capability Framework */}
      <section id="framework" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl lg:text-4xl mb-4">
              The Integrated.Social IMC Framework
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              Five capabilities. One program. Every channel feeding the same attribution model.
            </p>
          </div>
          <div className="space-y-6">
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div
                  key={cap.number}
                  className={`border ${cap.border} ${cap.bg} rounded-xl p-6 lg:p-8`}
                >
                  <div className="grid lg:grid-cols-3 gap-6 items-start">
                    <div className="lg:col-span-2">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-10 h-10 rounded-lg ${cap.bg} border ${cap.border} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${cap.color}`} />
                        </div>
                        <div>
                          <span className={`text-xs font-mono ${cap.color} opacity-60`}>{cap.number}</span>
                          <h3 className={`text-xl font-bold ${cap.color}`}>{cap.title}</h3>
                        </div>
                      </div>
                      <p className="text-white/70 leading-relaxed">{cap.description}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/40 uppercase tracking-wider font-medium mb-3">What we measure</p>
                      <ul className="space-y-2">
                        {cap.outcomes.map((o, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                            <CheckCircle className={`w-3.5 h-3.5 ${cap.color} mt-0.5 shrink-0`} />
                            {o}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">IMC agency vs. specialist agency vs. in-house</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              The model that is right for your business depends on your sales cycle, buying committee complexity, and attribution requirements. Here is an honest comparison.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-white/40 font-medium w-1/4">Dimension</th>
                  <th className="text-left py-4 px-4 text-teal-400 font-semibold w-1/4">
                    IMC Agency (Integrated.Social)
                  </th>
                  <th className="text-left py-4 px-4 text-white/60 font-medium w-1/4">Specialist Agency</th>
                  <th className="text-left py-4 px-4 text-white/60 font-medium w-1/4">In-House Team</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className={`border-b border-white/5 ${i % 2 === 0 ? "bg-white/[0.01]" : ""}`}>
                    <td className="py-4 px-4 text-white/50 font-medium">{row.dimension}</td>
                    <td className="py-4 px-4 text-white/90">{row.imc}</td>
                    <td className="py-4 px-4 text-white/50">{row.specialist}</td>
                    <td className="py-4 px-4 text-white/50">{row.inhouse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-white/60 text-sm leading-relaxed">{IMC_PRICE_LINE}</p>
        </div>
      </section>

      {/* Buyer's Guide: Five Questions */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Five questions to ask any agency claiming to be integrated</h2>
            <p className="text-white/60">
              These questions have specific, testable answers. If an agency cannot answer them clearly, they are not genuinely integrated.
            </p>
          </div>
          <div className="space-y-6">
            {buyerQuestions.map((item, i) => (
              <div key={i} className="border border-white/10 rounded-xl p-6 bg-white/[0.02]">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-teal-500/10 border border-teal-500/20 flex items-center justify-center shrink-0 text-teal-400 text-sm font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-white font-medium mb-2 leading-relaxed">"{item.question}"</p>
                    <p className="text-white/50 text-sm leading-relaxed">
                      <span className="text-teal-400/70 font-medium">Why this matters: </span>
                      {item.why}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content Cluster */}
      <section className="py-16 px-4 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center">IMC in the AI Era: Further Reading</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link href="/blog/integrated-marketing-communications-agency-ai-seo-aeo-london-2026/">
              <a className="block border border-white/10 rounded-xl p-6 bg-white/[0.02] hover:border-teal-400/30 transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mb-4">
                  <TrendingUp className="w-4 h-4 text-teal-400" />
                </div>
                <h3 className="font-semibold text-white group-hover:text-teal-400 transition-colors mb-2">
                  What an IMC Agency Looks Like in the AI Era
                </h3>
                <p className="text-sm text-white/50">The flagship PoV article on why the five-invoice model is broken and what replaces it.</p>
              </a>
            </Link>
            <Link href="/services/seo-aeo-geo/">
              <a className="block border border-white/10 rounded-xl p-6 bg-white/[0.02] hover:border-blue-400/30 transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4">
                  <Search className="w-4 h-4 text-blue-400" />
                </div>
                <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors mb-2">
                  SEO, AEO and GEO Services
                </h3>
                <p className="text-sm text-white/50">The full technical detail on how we build entity authority and AI citation coverage.</p>
              </a>
            </Link>
            <Link href="/services/gemini-agentic-ai/">
              <a className="block border border-white/10 rounded-xl p-6 bg-white/[0.02] hover:border-purple-400/30 transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-4">
                  <Bot className="w-4 h-4 text-purple-400" />
                </div>
                <h3 className="font-semibold text-white group-hover:text-purple-400 transition-colors mb-2">
                  Agentic AI Marketing Automation
                </h3>
                <p className="text-sm text-white/50">How AI agents support citation monitoring and content drafting within the IMC program, with human sign-off.</p>
              </a>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center">All five capabilities, individually</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { href: "/services/seo-aeo-geo/", icon: Search, color: "text-blue-400", label: "SEO, AEO and GEO" },
              { href: "/services/google-ai-mode-optimisation/", icon: MessageSquare, color: "text-teal-400", label: "Google AI Mode Optimization" },
              { href: "/services/preferred-sources-strategy/", icon: Globe, color: "text-green-400", label: "Preferred Sources Strategy" },
              { href: "/services/ppc-performance-max/", icon: BarChart2, color: "text-orange-400", label: "PPC and Performance Max" },
              { href: "/services/gemini-agentic-ai/", icon: Bot, color: "text-purple-400", label: "Agentic AI Automation" },
              { href: "/services/ai-marketing-strategy/", icon: Target, color: "text-pink-400", label: "AI Marketing Strategy" },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <Link key={s.href} href={s.href}>
                  <a className="flex items-center gap-3 border border-white/10 rounded-lg px-4 py-3 hover:border-white/20 transition-colors group">
                    <Icon className={`w-4 h-4 ${s.color} shrink-0`} />
                    <span className="text-sm text-white/70 group-hover:text-white transition-colors">{s.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-white/20 group-hover:text-white/50 ml-auto transition-colors" />
                  </a>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-white/[0.02] px-4 py-20" aria-labelledby="imc-faq-heading">
        <div className="mx-auto max-w-4xl">
          <p className="text-center text-xs font-mono uppercase tracking-widest text-teal-400">Direct answers</p>
          <h2 id="imc-faq-heading" className="mt-3 text-center font-serif text-3xl">Integrated marketing communications FAQs</h2>
          <p data-faq-summary className="mt-4 text-white/65 leading-relaxed">{IMC_FAQ_SUMMARY}</p>
          <FaqAccordion
            items={faqSchema}
            className="mt-10 space-y-3"
            itemClassName="overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]"
            buttonClassName="flex w-full items-center justify-between gap-4 px-6 py-5 text-left hover:bg-white/[0.035]"
            questionClassName="m-0 text-base font-semibold text-white"
            answerClassName="px-6 pb-5"
            answerTextClassName="text-sm leading-relaxed text-white/65"
            iconClassName="h-5 w-5 shrink-0 text-teal-300 transition-transform duration-200"
          />
        </div>
      </section>

      {/* Pricing and CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-900/20 via-[#0a0a0f] to-purple-900/10 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 text-teal-400 text-sm font-medium mb-6">
            <Zap className="w-3.5 h-3.5" />
            Outcome-based and pay-per-resolution pricing available
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl mb-6">
            Ready to consolidate your agency relationships?
          </h2>
          <p className="text-lg text-white/70 mb-4 leading-relaxed">
            {IMC_PRICE_LINE} We also offer outcome-based and pay-per-resolution models for clients who prefer to align fees with commercial results.
          </p>
          <p className="text-white/50 mb-10">
            The Free AI Visibility Score is the right starting point. It gives a 0–100 baseline for your own site before any scope is agreed.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/free-ai-growth-audit/" onClick={() => trackSectionCtaClick({ section_name: "service_imc_bottom_cta", cta_text: SCORE_LABEL, destination: "/free-ai-growth-audit/" })}>
              <a className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold px-8 py-4 rounded-lg transition-colors text-lg">
                {SCORE_LABEL}
                <ArrowRight className="w-5 h-5" />
              </a>
            </Link>
            <Link href="/blog/integrated-marketing-communications-agency-ai-seo-aeo-london-2026/">
              <a className="inline-flex items-center gap-2 border border-white/20 hover:border-teal-400/50 text-white/80 hover:text-white px-8 py-4 rounded-lg transition-colors text-lg">
                Read the IMC Framework Article
              </a>
            </Link>
          </div>
        </div>
      </section>

        {/* Arabic-aware share row */}
        <div className="container max-w-5xl mx-auto px-4 pb-16">
          <SharePostRow
            title="Integrated Marketing Communications"
            canonicalUrl="https://integrated.social/services/integrated-marketing-communications/"
            excerpt="One message, one buyer persona and one attribution model across SEO, AEO, GEO, PPC and agentic AI."
          />
        </div>
        <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
        </main>
      {/* Arabic translation toggle */}
      <ArabicToggle />
    </div>
  );
}
